import { useState } from "react";
import { toast } from "sonner";
import { User, Mail, Phone, Building2, FileDigit, MapPin, Save, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ContaPerfil() {
    const { user, refreshMe } = useAuth();
    const [form, setForm] = useState({
        name: user?.name || "",
        phone: user?.phone || "",
        company: user?.company || "",
        nif: user?.nif || "",
        address: user?.address || "",
    });
    const [pw, setPw] = useState({ current_password: "", new_password: "" });
    const [saving, setSaving] = useState(false);
    const [savingPw, setSavingPw] = useState(false);

    function onChange(e) {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    }
    function onPwChange(e) {
        setPw((p) => ({ ...p, [e.target.name]: e.target.value }));
    }

    async function saveProfile(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch("/auth/me", {
                name: form.name,
                phone: form.phone || null,
                company: form.company || null,
                nif: form.nif || null,
                address: form.address || null,
            });
            await refreshMe();
            toast.success("Perfil actualizado.");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao guardar perfil.");
        } finally {
            setSaving(false);
        }
    }

    async function changePassword(e) {
        e.preventDefault();
        if (pw.new_password.length < 8) {
            toast.error("A nova password tem de ter no mínimo 8 caracteres.");
            return;
        }
        setSavingPw(true);
        try {
            await api.post("/auth/change-password", pw);
            toast.success("Password alterada.");
            setPw({ current_password: "", new_password: "" });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao alterar password.");
        } finally {
            setSavingPw(false);
        }
    }

    return (
        <div data-testid="conta-perfil">
            <h2 className="font-heading text-2xl font-extrabold text-primary">Perfil</h2>
            <p className="text-sm text-muted-foreground">Mantenha os seus dados actualizados para comunicações e facturação.</p>

            <form onSubmit={saveProfile} className="mt-8 rounded-2xl border border-border bg-white p-6 lg:p-6 sm:p-8 grid gap-5 sm:grid-cols-2" data-testid="perfil-form">
                <PField icon={User} label="Nome" name="name" value={form.name} onChange={onChange} required testid="perfil-name" />
                <PField icon={Mail} label="Email" name="email" value={user?.email || ""} onChange={() => {}} disabled />
                <PField icon={Phone} label="Telemóvel" name="phone" value={form.phone} onChange={onChange} testid="perfil-phone" />
                <PField icon={Building2} label="Empresa" name="company" value={form.company} onChange={onChange} testid="perfil-company" />
                <PField icon={FileDigit} label="NIF" name="nif" value={form.nif} onChange={onChange} testid="perfil-nif" />
                <PField icon={MapPin} label="Morada" name="address" value={form.address} onChange={onChange} testid="perfil-address" />
                <div className="sm:col-span-2 flex justify-end">
                    <button type="submit" disabled={saving}
                        data-testid="perfil-save"
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition disabled:opacity-60">
                        <Save className="h-4 w-4" /> {saving ? "A guardar..." : "Guardar alterações"}
                    </button>
                </div>
            </form>

            {/* Password change */}
            <form onSubmit={changePassword} className="mt-8 rounded-2xl border border-border bg-white p-6 lg:p-6 sm:p-8 grid gap-5 sm:grid-cols-2" data-testid="password-form">
                <div className="sm:col-span-2 flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-primary" />
                    <h3 className="font-heading text-lg font-extrabold text-primary">Alterar password</h3>
                </div>
                <PField label="Password actual" name="current_password" type="password" value={pw.current_password} onChange={onPwChange} required testid="pw-current" />
                <PField label="Nova password (mín. 8)" name="new_password" type="password" value={pw.new_password} onChange={onPwChange} required testid="pw-new" />
                <div className="sm:col-span-2 flex justify-end">
                    <button type="submit" disabled={savingPw}
                        data-testid="pw-submit"
                        className="inline-flex items-center gap-2 rounded-md border border-primary px-5 py-3 text-sm font-bold text-primary hover:bg-primary hover:text-primary-foreground transition disabled:opacity-60">
                        {savingPw ? "A alterar..." : "Alterar password"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function PField({ icon: Icon, label, name, value, onChange, type = "text", required, disabled, testid }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
            <div className="mt-2 relative">
                {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />}
                <input
                    type={type} name={name} value={value} onChange={onChange} required={required} disabled={disabled}
                    data-testid={testid}
                    className={`w-full rounded-md border border-border bg-muted ${Icon ? "pl-10" : "pl-3"} pr-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white disabled:opacity-60 disabled:cursor-not-allowed`}
                />
            </div>
        </div>
    );
}
